import { getTranslation } from 'helpers/getTranslation';

import {
  eventNameToTranslationKey,
  getAnalyticsEventBaseName,
} from './analyticsEventUtils';

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

/**
 * Dictionary lookup that never leaks the raw value: `null` when there is no
 * translation, so callers hide the part instead of printing `trainer` / `token_exchange`.
 */
const translateDictionaryValue = (
  dictionary: string,
  value: string | null | undefined,
): string | null => {
  const normalized = readNonEmptyString(value)?.toLowerCase();

  if (!normalized) {
    return null;
  }

  const key = `student_page.timeline.${dictionary}.${normalized}`;
  const translated = getTranslation(key);

  return translated === key ? null : translated;
};

/** Provider / method name in the nominative: «Google», «Яндекс», «почта». */
export const translateAuthMethod = (method: string): string | null =>
  translateDictionaryValue('auth_methods', method);

/** Method as an adverbial phrase: «по почте», «через Google», «по промокоду». */
export const translateAuthMethodVia = (method: string): string | null =>
  translateDictionaryValue('auth_methods_via', method);

/** Why the sign-in screen appeared, as a sentence; `unknown` means no reason. */
export const translateAuthSource = (source: string): string | null =>
  source.toLowerCase() === 'unknown'
    ? null
    : translateDictionaryValue('auth_sources', source);

export const translateOtpErrorType = (errorType: string): string | null =>
  errorType.toLowerCase() === 'unknown'
    ? null
    : translateDictionaryValue('otp_errors', errorType);

/** Where a paywall / purchase button was shown: «в аркаде», «в списке тренингов». */
export const translatePlacement = (source: string): string | null =>
  translateDictionaryValue('placement', source);

/** Why a paywall was shown (`paywall_trigger` on web, `trigger` in the plugin). */
export const translatePaywallTrigger = (trigger: string): string | null =>
  trigger.toLowerCase() === 'unknown'
    ? null
    : translateDictionaryValue('paywall_triggers', trigger);

const PLAN_PERIOD_ALIASES: Record<string, string> = {
  week: 'week',
  weekly: 'week',
  pro_weekly: 'week',
  month: 'month',
  monthly: 'month',
  pro_monthly: 'month',
  pro_monthly_first_days: 'month',
  year: 'year',
  yearly: 'year',
  annual: 'year',
  annually: 'year',
  pro_annually: 'year',
  pro_yearly: 'year',
};

/** «Неделя» / «Месяц» / «Год» for plan ids, price lookup keys and `plan` values. */
export const translatePlanPeriod = (planId: string): string | null => {
  const period = PLAN_PERIOD_ALIASES[planId.trim().toLowerCase()];

  return period ? translateDictionaryValue('plans', period) : null;
};

export const translatePaymentErrorCode = (errorCode: string): string | null =>
  translateDictionaryValue('payment_errors', errorCode);

export const translateCheckoutFailureStage = (stage: string): string | null =>
  translateDictionaryValue('checkout_failure_stages', stage);

export const translateSubscriptionCancellationType = (
  cancellationType: string,
): string | null =>
  translateDictionaryValue('subscription_cancellation_types', cancellationType);

export const translateTrainerMode = (mode: string): string | null =>
  translateDictionaryValue('trainer_modes', mode);

/** Answer the student picked in the web onboarding survey (option ids like `goal-scratch`). */
export const translateOnboardingOption = (optionKey: string): string | null =>
  translateDictionaryValue('onboarding_options', optionKey);

export const resolveAuthMethodFromProperties = (
  properties?: Record<string, unknown>,
): string | null =>
  readNonEmptyString(properties?.method) ??
  readNonEmptyString(properties?.auth_method) ??
  readNonEmptyString(properties?.provider);

/** Auth source of an auth event; `server` / `unknown` are not reasons. */
export const resolveAuthSourceFromProperties = (
  properties?: Record<string, unknown>,
): string | null => {
  const source = readNonEmptyString(properties?.source);

  if (!source || source === 'server' || source.toLowerCase() === 'unknown') {
    return null;
  }

  return source;
};

const AUTH_METHOD_IN_TITLE_KEYS = new Set([
  'auth_method_clicked',
  'auth_social_clicked',
  'login_completed',
  'signup_completed',
  'oauth_viewed',
  'oauth_completed',
  'oauth_failed',
  'oauth_retry_clicked',
]);

const AUTH_SOURCE_KEYS = new Set(['auth_viewed', 'login_screen_viewed']);

/**
 * Events where `source` / `method` are sign-in values. Everywhere else the same
 * property names mean something else (paywall placement, trainer mode…).
 */
const AUTH_EVENT_KEYS = new Set([
  ...AUTH_METHOD_IN_TITLE_KEYS,
  ...AUTH_SOURCE_KEYS,
  'otp_code_sent',
  'otp_code_verified',
  'otp_failed',
]);

const toTranslationKey = (eventName: string) =>
  eventNameToTranslationKey(getAnalyticsEventBaseName(eventName));

export const isAuthMethodContextEvent = (eventName: string): boolean =>
  AUTH_METHOD_IN_TITLE_KEYS.has(toTranslationKey(eventName));

/** Sign-in screen events whose `source` explains why the student had to sign in. */
export const isAuthSourceContextEvent = (eventName: string): boolean =>
  AUTH_SOURCE_KEYS.has(toTranslationKey(eventName));

export const isAuthEvent = (eventName: string): boolean =>
  AUTH_EVENT_KEYS.has(toTranslationKey(eventName));

export const formatAuthPropertySuffix = (
  eventName: string,
  properties?: Record<string, unknown>,
): string | null => {
  if (isAuthMethodContextEvent(eventName)) {
    return resolveAuthMethodFromProperties(properties);
  }

  if (isAuthSourceContextEvent(eventName)) {
    return resolveAuthSourceFromProperties(properties);
  }

  return null;
};
